import { Component, EventEmitter, HostListener, Input, OnInit, Output, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { FileServerModel } from 'src/app/models/file-server.model';
import { UploaderSettingModel } from 'src/app/models/uploaderSetting.model';
import { FileServerService } from 'src/app/services/file-server.service';
import { ToastService } from 'src/app/services/toast.service';
import { NgClass } from '@angular/common';

interface FileItem {
  file: File | null;
  preview: string;
}

@Component({
  selector: 'app-file-uploader',
  templateUrl: './file-uploader.component.html',
  styleUrls: ['./file-uploader.component.scss'],
  standalone: true,
  imports: [NgClass]
})
export class FileUploaderComponent implements OnInit {
  private readonly FileServerSrv = inject(FileServerService);
  toastr = inject(ToastService);

  selectedFiles: FileItem[] = [];
  error: string = '';
  dragAreaClass: string = 'dragarea';

  @Input() description: string = '';
  @Input() name: string = '';
  @Input() settings!: UploaderSettingModel;

  @Output() deleteFileEvent = new EventEmitter<any>();
  @Output() sendImgsToParent = new EventEmitter<FileServerModel>();

  ngOnInit(): void {
    this.applyDefaultSettings();
    this.listenToFormControlChanges();
  }

  private applyDefaultSettings() {
    this.settings.multiple = this.settings.multiple ?? false;
    this.settings.autoSave = this.settings.autoSave ?? false;
    this.settings.hideDelete = this.settings.hideDelete ?? false;
    this.settings.inputWhith = this.settings.inputWhith ?? '100%';
    this.settings.imgWhith = this.settings.imgWhith ?? '120px';
    this.settings.imgHeight = this.settings.imgHeight ?? '120px';
  }

  private listenToFormControlChanges() {
    const control = this.settings.formGroup?.controls[this.name];
    if (control) {
      // Si ya tiene valor al iniciar (Carga inicial)
      if (control.value) {
        this.reconstructFiles(control.value);
      }

      control.valueChanges.subscribe((val: any) => {
        if (!val) {
          this.selectedFiles = [];
          this.error = '';
        } else {
          this.reconstructFiles(val);
        }
      });
    }
  }

  // Nueva función auxiliar para reconstruir la lista
  private reconstructFiles(val: any) {
    // Si tenemos el array de previsualizaciones, lo usamos
    if (val.allPreviews && Array.isArray(val.allPreviews)) {
      this.selectedFiles = val.allPreviews.map((p: string) => ({
        file: null, // El archivo ya está en el server/memoria, no hace falta el File
        preview: p
      }));
    }
    // Si es un valor viejo que solo tiene imageSrc
    else if (val.imageSrc && this.selectedFiles.length === 0) {
      this.selectedFiles = [{ file: null, preview: val.imageSrc }];
    }
  }

  onFileChange(event: any) {
    const files: FileList = event.target.files;
    this.processFiles(files);
  }

  @HostListener('dragover', ['$event']) onDragOver(event: any) {
    event.preventDefault();
    this.dragAreaClass = 'droparea';
  }

  @HostListener('dragleave', ['$event']) onDragLeave(event: any) {
    event.preventDefault();
    this.dragAreaClass = 'dragarea';
  }

  @HostListener('drop', ['$event']) onDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.dragAreaClass = 'dragarea';
    if (event.dataTransfer?.files) {
      this.processFiles(event.dataTransfer.files);
    }
  }

  private processFiles(files: FileList) {
    this.error = '';
    const fileArray = Array.from(files);

    if (!this.settings.multiple) {
      // Si solto mas de uno
      if (fileArray.length > 1) {
        this.error = this.settings.multipleErrorMessage ?? 'Solo se permite una imagen';
        return;
      }
      // Si ya existe una imagen previa
      if (this.selectedFiles.length > 0) {
        this.error = 'Ya existe una imagen. Eliminá la actual para subir otra.';
        return;
      }
    }

    fileArray.forEach(file => {
      // Validacion de tamaño
      if (this.settings.validationMbMaxSize && (file.size / 1024 ** 2) > this.settings.validationMbMaxSize) {
        this.error = `El archivo "${file.name}" es demasiado grande`;
        return;
      }

      // Validacion de tipo
      if (this.settings.accept && !this.settings.accept.includes(file.type)) {
        this.error = this.settings.acceptErrorMessage ?? 'Formato no permitido';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.selectedFiles.push({ file: file, preview: e.target.result });
        this.updateComponentState();
      };
      reader.readAsDataURL(file);
    });
  }

  removeFile(index: number) {
    const control = this.settings.formGroup.controls[this.name];

    if (control.value?.id && index === 0 && !this.settings.multiple) {
      this.FileServerSrv.delete(control.value.id).subscribe();
    }

    this.selectedFiles.splice(index, 1);
    this.updateComponentState();
    this.deleteFileEvent.emit({ index, deleted: true });
  }

  private resetComponent() {
    this.selectedFiles = [];
    this.error = '';
    const control = this.settings.formGroup?.controls[this.name];
    if (control) {
      control.reset(null);
    }
  }

  saveFile(model: FileServerModel): Observable<any> {
    return this.FileServerSrv.UploadTaskFile(
      model.groupId,
      model,
      this.settings.controller
    ).pipe(
      tap({
        next: (data) => {
          this.toastr.success('Archivo enviado', 'El archivo se ha enviado correctamente.');
          this.resetComponent();
        },
        error: () => {
          this.toastr.error('Error', 'No se pudo enviar el archivo.');
        }
      })
    );
  }

  private updateComponentState() {
    const control = this.settings.formGroup?.controls[this.name];

    if (this.selectedFiles.length === 0) {
      control?.patchValue(null);
      this.sendImgsToParent.emit(undefined);
      return;
    }

    const formData = new FormData();
    const allPreviews = this.selectedFiles.map(f => f.preview);

    this.selectedFiles.forEach((item) => {
      if (item.file) {
        // FileServer binds IFormFileCollection 'files' (IA-5092)
        formData.append('files', item.file, item.file.name);
      }
    });

    const model: any = {
      id: undefined,
      groupId: this.settings.formGroup?.controls['id']?.value,
      name: this.name,
      fileList: this.selectedFiles.map(f => f.file).filter(f => f !== null) as any,
      imageSrc: allPreviews[0],
      allPreviews: allPreviews,
      formData: formData,
    };

    if (control) {
      control.patchValue(model, { emitEvent: false });
    }

    this.sendImgsToParent.emit(model);

    if (this.settings.autoSave && this.selectedFiles.some(f => f.file !== null)) {
      this.saveFile(model).subscribe();
    }
  }

  getControlValue() {
    return this.settings.formGroup?.controls[this.name]?.value;
  }
}